import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageShell, SlideCorner } from "@/components/Layout";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

/* ============================================================
   /build/artist — Project-first intake.
   "We're considering projects" — not "we offer services".
   Six-question collaborative form.
   ============================================================ */

const PROJECT_INTENTS = [
  { v: "releasing_music",  l: "I'm releasing music" },
  { v: "making_film",      l: "I'm making a film" },
  { v: "writing_book",     l: "I'm writing a book" },
  { v: "art_exhibition",   l: "I'm building an art exhibition" },
  { v: "other",            l: "I have something else" },
];

const DISCIPLINE_OPTIONS = [
  { v: "music",       l: "Music" },
  { v: "visual_art",  l: "Visual Art" },
  { v: "film",        l: "Film" },
  { v: "writing",     l: "Writing" },
  { v: "performance", l: "Performance" },
  { v: "other",       l: "Other" },
];

const STAGE_OPTIONS = [
  { v: "idea",             l: "Idea" },
  { v: "planning",         l: "Planning" },
  { v: "in_production",    l: "Already in production" },
  { v: "launching_soon",   l: "Launching soon" },
  { v: "already_released", l: "Already released" },
];

const NEEDS_OPTIONS = [
  { v: "creative_strategy", l: "Creative Strategy" },
  { v: "production",        l: "Production" },
  { v: "marketing",         l: "Marketing" },
  { v: "press",             l: "Press" },
  { v: "partnerships",      l: "Partnerships" },
  { v: "distribution",      l: "Distribution" },
  { v: "funding",           l: "Funding" },
  { v: "operations",        l: "Operations" },
  { v: "other",             l: "Something else" },
];

export default function BuildArtist() {
  const [form, setForm] = useState({
    project_intent: "",
    project_summary: "",
    discipline: "",
    work_stage: "",
    needs: [],
    brief: "",
    contact_name: "",
    contact_email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggleNeed = (v) => {
    setForm((f) => ({
      ...f,
      needs: f.needs.includes(v) ? f.needs.filter((x) => x !== v) : [...f.needs, v],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_email) { toast.error("We need your email to reply"); return; }
    if (!form.project_summary) { toast.error("Tell us what you're building"); return; }
    if (!form.brief) { toast.error("A short description helps us respond meaningfully"); return; }
    setSubmitting(true);
    try {
      await api.post("/briefs", {
        intent: "artist",
        company: form.contact_name || "Independent artist",
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        project_type: form.project_summary,
        budget_range: "",
        timeline: form.work_stage || "",
        brief: form.brief,
        disciplines: form.discipline ? [form.discipline] : [],
        project_intent: form.project_intent,
        work_stage: form.work_stage,
        needs: form.needs,
      });
      setDone(true);
      toast.success("Received. We'll be in touch.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="BUILD · FOR ARTISTS" />
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-16 pb-16">
          <Link to="/build" className="meta-mono !text-white/55 hover:!text-white transition inline-flex items-center gap-2 mb-10" data-testid="build-artist-back">
            <ArrowLeft size={12} /> BACK TO BUILD
          </Link>
          <p className="meta-mono !text-primary mb-6">— FOR INDEPENDENT ARTISTS</p>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[0.9] tracking-tighter">
            Build <span className="hl-cyan">alongside</span> us.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-base md:text-lg leading-relaxed">
            We&rsquo;re not offering a service menu. We&rsquo;re considering projects — the ones we can meaningfully help move forward. Answer these six, and someone from the team will get back within 3 working days.
          </p>
        </div>
      </section>

      {/* WHAT WE / YOU BRING */}
      <section className="border-b border-border">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-20">
          <div className="grid md:grid-cols-2 gap-16">
            <div data-testid="artist-what-we-bring">
              <p className="meta-mono !text-primary mb-6">— WHAT WE BRING</p>
              <ul className="space-y-4">
                <li className="font-display text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight text-white">Produce</li>
                <li className="font-display text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight text-white">Strategy</li>
                <li className="font-display text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight text-white">Distribution</li>
              </ul>
            </div>
            <div data-testid="artist-what-you-bring">
              <p className="meta-mono !text-primary mb-6">— WHAT YOU BRING</p>
              <p className="font-display text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight text-white">
                The <span className="hl-coral">work.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* THE FORM */}
      <section className="border-b border-border">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-24 md:py-32">
          {done ? (
            <div className="border border-secondary/40 bg-secondary/[0.04] p-10" data-testid="build-artist-success">
              <p className="label-mono !text-secondary mb-4">✓ RECEIVED</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">Thanks. We&apos;ll be in touch <span className="hl-cyan">shortly.</span></h2>
              <p className="mt-6 text-white/75">Someone from the team reads every message personally. Expect a reply within 3 working days at <strong>{form.contact_email}</strong>.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-16" data-testid="build-artist-form">

              {/* Q1 — Project intent (radio) */}
              <QBlock number="01" label="What are you working on?">
                <div className="space-y-3" data-testid="artist-project-intent">
                  {PROJECT_INTENTS.map((o) => (
                    <RadioRow key={o.v} name="project_intent" value={o.v} checked={form.project_intent === o.v} onChange={(v) => setForm({ ...form, project_intent: v })} label={o.l} />
                  ))}
                </div>
              </QBlock>

              {/* Q2 — Short answer */}
              <QBlock number="02" label="What are you building?">
                <input
                  data-testid="artist-project-summary"
                  value={form.project_summary}
                  onChange={(e) => setForm({ ...form, project_summary: e.target.value })}
                  placeholder="In one line…"
                  className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-3 text-lg md:text-2xl font-display font-medium"
                />
              </QBlock>

              {/* Q3 — Discipline dropdown */}
              <QBlock number="03" label="Which best describes your project?">
                <select
                  data-testid="artist-discipline"
                  value={form.discipline}
                  onChange={(e) => setForm({ ...form, discipline: e.target.value })}
                  className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-3 text-lg md:text-2xl font-display font-medium appearance-none cursor-pointer"
                >
                  <option value="" className="bg-background">Pick one…</option>
                  {DISCIPLINE_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v} className="bg-background">{o.l}</option>
                  ))}
                </select>
              </QBlock>

              {/* Q4 — Stage */}
              <QBlock number="04" label="Where are you currently?">
                <div className="space-y-3" data-testid="artist-work-stage">
                  {STAGE_OPTIONS.map((o) => (
                    <RadioRow key={o.v} name="work_stage" value={o.v} checked={form.work_stage === o.v} onChange={(v) => setForm({ ...form, work_stage: v })} label={o.l} />
                  ))}
                </div>
              </QBlock>

              {/* Q5 — Needs (checkboxes) */}
              <QBlock number="05" label="What do you need help with?">
                <p className="text-sm text-white/50 -mt-3 mb-4">Tick as many as apply.</p>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="artist-needs">
                  {NEEDS_OPTIONS.map((o) => (
                    <CheckboxRow key={o.v} checked={form.needs.includes(o.v)} onChange={() => toggleNeed(o.v)} label={o.l} testid={`artist-need-${o.v}`} />
                  ))}
                </div>
              </QBlock>

              {/* Q6 — Tell us about it */}
              <QBlock number="06" label="Tell us about it.">
                <textarea
                  data-testid="artist-brief"
                  value={form.brief}
                  onChange={(e) => setForm({ ...form, brief: e.target.value })}
                  rows={7}
                  placeholder="The ambition, the collaborators, what&rsquo;s already in place, what&rsquo;s in your way. Say more than you think you should."
                  className="w-full bg-transparent border border-border p-4 outline-none focus:border-primary text-base leading-relaxed"
                />
              </QBlock>

              {/* Contact */}
              <div className="border-t border-border pt-12 grid md:grid-cols-2 gap-x-6 gap-y-4">
                <ContactField label="Your name" v={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="artist-name" />
                <ContactField label="Email" type="email" v={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="artist-email" />
              </div>

              <button data-testid="artist-submit" disabled={submitting} className="btn-pill btn-coral w-full justify-center py-5 text-base disabled:opacity-40 disabled:cursor-not-allowed">
                {submitting ? "Sending…" : <>Send it in <ArrowRight size={14} /></>}
              </button>
              <p className="meta-mono !text-white/45 text-center">— WE READ EVERY MESSAGE. REPLY IN 3 WORKING DAYS.</p>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
}

/* ============ atoms ============ */

function QBlock({ number, label, children }) {
  return (
    <div>
      <div className="flex items-start gap-4 md:gap-6 mb-6">
        <span className="meta-mono !text-primary pt-1.5 shrink-0">{number}</span>
        <h3 className="font-display text-2xl md:text-4xl font-bold leading-[1.05] tracking-tight text-white">{label}</h3>
      </div>
      <div className="md:pl-14">{children}</div>
    </div>
  );
}

function RadioRow({ name, value, checked, onChange, label }) {
  return (
    <label className={`flex items-center gap-4 p-4 border cursor-pointer transition ${checked ? "border-primary bg-primary/[0.06]" : "border-border hover:border-white/30"}`}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="sr-only"
      />
      <span className={`inline-block w-3 h-3 rounded-full border ${checked ? "border-primary bg-primary" : "border-white/40"}`} />
      <span className={`text-base ${checked ? "text-white" : "text-white/80"}`}>{label}</span>
    </label>
  );
}

function CheckboxRow({ checked, onChange, label, testid }) {
  return (
    <label className={`flex items-center gap-3 p-3 border cursor-pointer transition ${checked ? "border-primary bg-primary/[0.06]" : "border-border hover:border-white/30"}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
        data-testid={testid}
      />
      <span className={`inline-block w-3 h-3 border ${checked ? "border-primary bg-primary" : "border-white/40"}`} />
      <span className={`text-sm ${checked ? "text-white" : "text-white/80"}`}>{label}</span>
    </label>
  );
}

function ContactField({ label, v, onChange, testid, type = "text" }) {
  return (
    <div>
      <p className="meta-mono !text-white/55 mb-2">— {label.toUpperCase()}</p>
      <input
        data-testid={testid}
        type={type}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-base font-light"
      />
    </div>
  );
}
